import { useNavigate, useLocation } from 'react-router';
import { useApp } from 'providers/useApp/AppContext';
import { guardedSessionStorage } from 'utils/fnStorage';

export const useNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { historyLength, appHistory } = useApp();

    const onBackPressed = (fallbackPath = '/', { skipSamePath = false }: { skipSamePath?: boolean } = {}) => {
        const returnPath = guardedSessionStorage.getItem('returnPath');
        if (returnPath) {
            guardedSessionStorage.removeItem('returnPath');
            guardedSessionStorage.removeItem('channelSwitchReturnPath');
            if (returnPath === 'back') {
                navigate(-1);
            } else {
                navigate(returnPath);
            }
            return;
        }

        if (historyLength < 2) {
            const storedReturnPath = guardedSessionStorage.getItem('channelSwitchReturnPath');
            guardedSessionStorage.removeItem('channelSwitchReturnPath');

            navigate(storedReturnPath ?? fallbackPath, { replace: true });
            return;
        }

        /** 
        This is a hack: Channel switching in MiniEpg uses replace navigation, which collapses multiple
        /play/ entries into one — making navigate(-1) insufficient to reach the pre-player
        page. Count consecutive /play/ entries in appHistory and skip past all of them.
        If appHistory is stale (React state timing on rapid PUSH→REPLACE), fall back to
        channelSwitchReturnPath stored in sessionStorage when first entering the player. 
        */
        const state = location.state as { channelSwitch?: boolean } | null;
        if (state?.channelSwitch) {
            let playerEntries = 0;
            for (let i = appHistory.length - 1; i >= 0; i -= 1) {
                if (appHistory[i].path.startsWith('/play/')) {
                    playerEntries += 1;
                } else {
                    break;
                }
            }
            const channelSwitchReturnPath = guardedSessionStorage.getItem('channelSwitchReturnPath');
            guardedSessionStorage.removeItem('channelSwitchReturnPath');

            if (playerEntries > 0) {
                navigate(-playerEntries);
            } else if (channelSwitchReturnPath) {
                // appHistory has no player entries — fall back to stored return path
                navigate(channelSwitchReturnPath, { replace: true });
            } else {
                navigate(fallbackPath, { replace: true });
            }
            return;
        }

        guardedSessionStorage.removeItem('channelSwitchReturnPath');

        if (skipSamePath) {
            let samePathEntries = 0;
            for (let i = appHistory.length - 1; i >= 0; i -= 1) {
                if (appHistory[i].path === location.pathname) {
                    samePathEntries += 1;
                } else {
                    break;
                }
            }

            if (samePathEntries === appHistory.length) {
                navigate(fallbackPath, { replace: true });
                return;
            }

            if (samePathEntries > 1) {
                navigate(-samePathEntries);
                return;
            }
        }

        navigate(-1);
    };

    return {
        navigate,
        location,
        onBackPressed,
    };
};
